import { useEffect, useState } from 'react';
import { Select, Chip, Card, EmptyState, MultiSelectChecklist } from '../../../../shared/components';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { ClientComparisonRow, ServiceType, AvailableMonth, MONTH_NAMES_ES } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const monthLabel = (m: string) => MONTH_LABELS[MONTH_NAMES_ES.indexOf(m)] ?? m;
const money = (v: number) => v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

type Metric = 'utilidad' | 'ingreso' | 'gastos';
const METRIC_LABEL: Record<Metric, string> = { utilidad: 'Utilidad', ingreso: 'Ingresos', gastos: 'Gastos' };

/**
 * Pestaña "Comparar Clientes" - entre "Datos Tabla" y "Tabla Resumen".
 * "Tabla Resumen" colapsa todos los clientes en un solo total de
 * Ingreso/Gastos/Utilidad; esta pantalla existe para lo contrario: dejar
 * cada cliente como su propia fila, rankeados de mayor a menor, para poder
 * comparar su desempeño entre sí.
 *
 * Por defecto muestra solo el ÚLTIMO MES CARGADO (no los 12 meses del año,
 * que en la práctica suelen venir vacíos si todavía no se han procesado) -
 * el filtro "Mes" es un checklist de selección múltiple (igual que Tipo
 * Servicio y Cliente): permite comparar 2 o más meses puntuales lado a lado,
 * o dejarlo vacío para ver todos los meses cargados de ese año.
 */
export function ClientComparisonTab() {
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [mesInitialized, setMesInitialized] = useState(false);
  const [tiposServicio, setTiposServicio] = useState<string[]>([]);
  const [metric, setMetric] = useState<Metric>('utilidad');
  const [allClients, setAllClients] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [rows, setRows] = useState<ClientComparisonRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([repo.listDistinctClients(), repo.listServiceTypes(), repo.listAvailableMonths()]).then(([clients, st, months]) => {
      setAllClients(clients);
      setServiceTypes(st);
      setAvailableMonths(months);
    });
  }, []);

  // Al cargar (o al cambiar de año), preselecciona automáticamente solo el
  // ÚLTIMO MES CARGADO de ese año - evita la confusión de ver columnas
  // vacías cuando en realidad solo hay un mes con datos. El usuario puede
  // luego marcar meses adicionales en el checklist para compararlos.
  useEffect(() => {
    if (mesInitialized) return;
    const monthsThisYear = availableMonths.filter((m) => m.anio === anio);
    if (monthsThisYear.length === 0) return;
    const latest = monthsThisYear.reduce((a, b) => (MONTH_NAMES_ES.indexOf(b.mes) > MONTH_NAMES_ES.indexOf(a.mes) ? b : a));
    setSelectedMonths([latest.mes]);
    setMesInitialized(true);
  }, [availableMonths, anio, mesInitialized]);

  useEffect(() => {
    setIsLoading(true);
    repo
      .getClientComparison({ anio, meses: selectedMonths.length > 0 ? selectedMonths : undefined, tiposServicio: tiposServicio.length > 0 ? tiposServicio : undefined, clientes: selectedClients.length > 0 ? selectedClients : undefined })
      .then((r) => {
        setRows(r);
        setIsLoading(false);
      });
  }, [anio, selectedMonths, tiposServicio, selectedClients]);

  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => ({ value: String(y), label: String(y) }));
  const serviceOptions = serviceTypes.map((s) => s.nombre);
  // Siempre en orden cronológico (enero -> diciembre), sin importar el orden en que se marcaron en el checklist.
  const monthsThisYear = availableMonths.filter((m) => m.anio === anio).sort((a, b) => MONTH_NAMES_ES.indexOf(a.mes) - MONTH_NAMES_ES.indexOf(b.mes));
  const monthKeyOptions = monthsThisYear.map((m) => m.mes);

  // Checklist vacío = todos los meses cargados de ese año (mismo criterio que "sin filtro" en Tipo Servicio/Cliente).
  const selectedSet = new Set(selectedMonths);
  const displayMonths = (selectedMonths.length > 0 ? monthsThisYear.filter((m) => selectedSet.has(m.mes)) : monthsThisYear).map((m) => m.mes);

  const valueFor = (r: ClientComparisonRow, mes: string) => r.valuesByMonth[mes]?.[metric] ?? 0;
  const totalFor = (r: ClientComparisonRow) => (metric === 'utilidad' ? r.totalUtilidad : metric === 'ingreso' ? r.totalIngreso : r.totalGastos);

  return (
    // Excepción deliberada al layout móvil-first del resto de la Suite (ver
    // "Tabla Resumen" para el mismo patrón): esta pestaña se muestra a todo
    // lo ancho de la pantalla en computador.
    <div className="relative left-1/2 -ml-[50vw] w-screen space-y-4 px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 lg:max-w-3xl">
        <Select
          label="Año"
          options={yearOptions}
          value={String(anio)}
          onChange={(e) => {
            setAnio(Number(e.target.value));
            setMesInitialized(false);
          }}
        />
        <MultiSelectChecklist
          label="Mes"
          options={monthKeyOptions}
          selected={selectedMonths}
          onApply={setSelectedMonths}
          renderLabel={monthLabel}
          allLabel="(Todos)"
          selectedSuffix="seleccionados"
        />
        <MultiSelectChecklist label="Tipo Servicio" options={serviceOptions} selected={tiposServicio} onApply={setTiposServicio} />
        <MultiSelectChecklist label="Cliente" options={allClients} selected={selectedClients} onApply={setSelectedClients} />
      </div>

      <div>
        <span className="mb-1.5 block text-sm font-medium text-ink-700">Comparar por</span>
        <div className="flex gap-2">
          {(['utilidad', 'ingreso', 'gastos'] as Metric[]).map((m) => (
            <Chip key={m} label={METRIC_LABEL[m]} active={metric === m} onClick={() => setMetric(m)} />
          ))}
        </div>
      </div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          icon="⚖️"
          title="Sin datos para comparar"
          description={monthsThisYear.length === 0 ? `Todavía no hay ningún mes procesado en ${anio} - carga y procesa un mes desde "Resultados del Mes" y "Datos Tabla" primero.` : 'Ajusta el mes o los filtros seleccionados.'}
        />
      ) : (
        <Card padded={false} className="scroll-accent overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 bg-ink-50/60 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                <th className="whitespace-nowrap px-4 py-3">Cliente</th>
                {displayMonths.map((m) => (
                  <th key={m} className="whitespace-nowrap px-4 py-3 text-right">
                    {monthLabel(m)}
                  </th>
                ))}
                {displayMonths.length > 1 && <th className="whitespace-nowrap px-4 py-3 text-right">Total ({METRIC_LABEL[metric]})</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const total = totalFor(r);
                return (
                  <tr key={r.cliente} className="border-b border-ink-50 last:border-0 hover:bg-ink-50/40">
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-ink-700">
                      <span className="mr-2 text-xs text-ink-400">#{i + 1}</span>
                      {r.cliente}
                    </td>
                    {displayMonths.map((m) => {
                      const v = valueFor(r, m);
                      return (
                        <td key={m} className={['whitespace-nowrap px-4 py-3 text-right', v < 0 ? 'text-critical' : 'text-ink-700'].join(' ')}>
                          {money(v)}
                        </td>
                      );
                    })}
                    {displayMonths.length > 1 && (
                      <td className={['whitespace-nowrap px-4 py-3 text-right font-semibold', total < 0 ? 'text-critical' : 'text-ink-900'].join(' ')}>{money(total)}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
